/**
 * Import processing (TO-397, Epic 25 F9): the planner, the strategies, the
 * atomic commit and the per-record change log of one import execution.
 *
 * The planner turns a file's validated canonical records into an ordered plan
 * of entries; the owning domain's importer checks and applies the actionable
 * ones; every entry is kept, with its outcome, in the execution's change log.
 */
import type { ImportExecutionStatus } from './import-execution.js'

/** What a plan entry does to one domain record (spec BR-3). `INVALID` entries are never applied. */
export enum ImportPlanAction {
	CREATE = 'CREATE',
	UPDATE = 'UPDATE',
	UNCHANGED = 'UNCHANGED',
	DEACTIVATE = 'DEACTIVATE',
	INVALID = 'INVALID',
}

/** What happened to a plan entry (spec BR-7, BR-8). `PENDING` until the importer answers. */
export enum ImportChangeOutcome {
	PENDING = 'PENDING',
	APPLIED = 'APPLIED',
	FAILED = 'FAILED',
	NO_OP = 'NO_OP',
	SKIPPED = 'SKIPPED',
	NOT_APPLIED = 'NOT_APPLIED',
}

/** Why the planner could not plan a record (spec BR-2). */
export enum ImportPlanErrorCode {
	KEY_MISSING = 'KEY_MISSING',
	DUPLICATE_KEY = 'DUPLICATE_KEY',
}

/** Whether a domain importer's `apply` committed or refused the plan (spec BR-8). */
export enum DomainImportApplyStatus {
	APPLIED = 'applied',
	REJECTED = 'rejected',
}

/** One actionable entry's outcome as a domain importer reports it (`atomic: false`). */
export enum ImportApplyOutcome {
	APPLIED = 'APPLIED',
	FAILED = 'FAILED',
}

/** A code the service records for an entry when the importer breaks the port contract (spec BR-8). */
export enum ImportApplyErrorCode {
	/** `apply` returned no outcome for an entry it was sent. */
	OUTCOME_MISSING = 'OUTCOME_MISSING',
}

/** A canonical field value: transforms have already normalized it. */
export type CanonicalValue = string | number | boolean | null

/** One record in canonical form, by canonical field name. */
export type CanonicalValues = Record<string, CanonicalValue>

/** One record of a file after canonical, dataset and business validation (F8). */
export interface ValidatedRecord {
	/** 1-based line of the record in the source file. */
	sourceLine: number
	values: CanonicalValues
}

/** One entry of a mutation plan (spec BR-3…BR-5). */
export interface ImportPlanEntry {
	seq: number
	/** The file record's line; `null` for an entry with no file record (`DEACTIVATE`). */
	sourceLine: number | null
	action: ImportPlanAction
	/** The `processing.key` values; `null` when the strategy matches no key (`append`, `replace`). */
	key: CanonicalValue[] | null
	before: CanonicalValues | null
	after: CanonicalValues | null
	changedFields: string[]
	/** `KEY_MISSING`, `DUPLICATE_KEY` or an importer's rejection code; `null` unless `INVALID`. */
	errorCode: string | null
}

/** Counts of an execution's plan entries (spec BR-15). */
export interface ImportProcessingSummary {
	total: number
	created: number
	updated: number
	unchanged: number
	deactivated: number
	invalid: number
	/** Entries whose outcome is `FAILED`. */
	failed: number
}

/** One row of an execution's change log, as F11 reads it. */
export interface ImportExecutionChangeDTO extends ImportPlanEntry {
	outcome: ImportChangeOutcome
}

/** What `process` answers (spec Contract). */
export interface ImportProcessingResult {
	status: ImportExecutionStatus.COMPLETED | ImportExecutionStatus.PROCESSING_FAILED
	failureCode: string | null
	summary: ImportProcessingSummary | null
	/** True when the execution had already finished and nothing was done. */
	alreadyProcessed: boolean
}
